import { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { documentApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import { sanitizeDocumentHtml } from "./sanitizeHtml";
import { sourceHost } from "./sourceHost";
import { isAgent } from "../tasks/userDisplay";
import SharingControl from "../sharing/SharingControl";

// What each setting means for a document: shared documents are read +
// copy, never edited outside their owner's side (household sharing Q5).
const DOCUMENT_VISIBILITY_HINTS = {
  private: "Only you and your agents can see it.",
  household:
    "Your household — its people and their agents — can read, search and copy it.",
  people:
    "The people it's shared with — and their agents — can read, search and copy it.",
};
import "../../styles/knowledge.css";

// A document's tag chips, with the AI-suggested vs confirmed distinction the
// spec requires the UI to show: a confirmed/approved tag is solid, an
// unreviewed AI suggestion is outlined and labeled.
export function TagChips({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags.map((tag) => {
        const settled = tag.confirmed || tag.status === "approved";
        return (
          <Badge
            key={tag.slug}
            variant={settled ? "success" : "outline"}
            title={
              settled
                ? "Confirmed tag"
                : "AI suggestion — review it under Library → Tags"
            }
          >
            {tag.name}
            {!settled && <span className="ml-1 font-normal">· AI</span>}
          </Badge>
        );
      })}
      <Link to="/knowledge/tags" className="text-sm text-primary underline">
        review tags
      </Link>
    </div>
  );
}

TagChips.propTypes = {
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      confirmed: PropTypes.bool,
    }),
  ),
};

function DocumentDetail() {
  // Shared library (S3): this document may have been saved by the owner or
  // by one of their agents. AppShell already resolved /users/me/.
  const { me } = useOutletContext() || {};
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editMarkdown, setEditMarkdown] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [related, setRelated] = useState([]);
  const [copying, setCopying] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetchDocument = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentApi.get(id);
      setDoc(response.data);
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to load the document."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  useEffect(() => {
    // Best-effort garnish: an empty or failed related-list is silently
    // nothing (documents without embeddings simply have no relations yet).
    setRelated([]); // don't show the PREVIOUS doc's list while this loads
    let cancelled = false;
    documentApi
      .related(id)
      .then((response) => {
        if (!cancelled) setRelated(response.data || []);
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isNote = doc?.source_type === "note";
  // The server says what this caller may do (household sharing: a
  // document shared with you is read + copy). Re-enrich is spend and is
  // gated like delete. Older payloads without the flags keep today's UI.
  const canEdit = doc?.can_edit ?? true;
  const canDelete = doc?.can_delete ?? true;

  const startEditing = () => {
    setEditTitle(doc.title || "");
    setEditMarkdown(doc.markdown_source || "");
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    const payload = isNote
      ? { title: editTitle.trim(), markdown_source: editMarkdown }
      : { title: editTitle.trim() };
    try {
      const response = await documentApi.update(doc.id, payload);
      setDoc(response.data);
      setEditing(false);
    } catch (err) {
      const parsed = parseApiError(err, "Could not save changes.");
      setSaveError(
        [
          parsed.formError,
          ...Object.entries(parsed.fieldErrors).map(
            ([field, value]) => `${field}: ${[].concat(value).join(", ")}`,
          ),
        ]
          .filter(Boolean)
          .join(" — "),
      );
    } finally {
      setSaving(false);
    }
  };

  // A plain visibility flip: the page shows what went wrong.
  const changeVisibility = async (changes) => {
    setActionError(null);
    try {
      const response = await documentApi.update(doc.id, changes);
      setDoc(response.data);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't change who can see it."));
    }
  };

  // The Share dialog saves through here and shows its own errors.
  const share = async (changes) => {
    const response = await documentApi.update(doc.id, changes);
    setDoc(response.data);
  };

  const copyToMine = async () => {
    setCopying(true);
    setActionError(null);
    try {
      const response = await documentApi.copy(doc.id);
      navigate(`/knowledge/documents/${response.data.id}`);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't copy it. Try again."));
    } finally {
      setCopying(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichError(null);
    try {
      const response = await documentApi.enrich(doc.id);
      setDoc(response.data);
    } catch (err) {
      setEnrichError(apiErrorMessage(err, "The retry failed."));
    } finally {
      setEnriching(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await documentApi.delete(doc.id);
      navigate("/knowledge/documents");
    } catch (err) {
      // Inside the dialog, NOT the page-level error — that view replaces the
      // document with a Retry button that re-fetches, which would misread as
      // "retry the delete" (L4 review finding). The dialog stays open so the
      // message sits next to the button that produced it.
      setDeleteError(apiErrorMessage(err, "Failed to delete the document."));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container mt-6 py-12 pt-4 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading document…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button size="sm" variant="outline" onClick={fetchDocument}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
        <Link to="/knowledge/documents" className="mt-2 inline-block text-sm">
          Back to Library
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: "860px" }}>
      <div className="mb-2">
        <Link
          to="/knowledge/documents"
          className="text-sm text-primary underline underline-offset-2"
        >
          ← Library
        </Link>
      </div>

      <div className="mb-1 flex items-start justify-between gap-2">
        <h1 className="m-0 font-display text-2xl font-semibold tracking-tight">
          {doc.title || "Untitled"}
        </h1>
        <div className="flex shrink-0 gap-2">
          {canEdit ? (
            <Button size="sm" variant="outline" onClick={startEditing}>
              {isNote ? "Edit" : "Rename"}
            </Button>
          ) : (
            <Button size="sm" onClick={copyToMine} disabled={copying}>
              {copying && <Loader2 className="animate-spin" />}
              Copy to my library
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setDeleteError(null);
                setShowDeleteModal(true);
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {isNote ? (
          <span>Note · saved {new Date(doc.created_at).toLocaleString()}</span>
        ) : (
          <span>
            Archived from{" "}
            <a
              href={doc.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              {sourceHost(doc.source_url) || doc.source_url}
            </a>{" "}
            on {new Date(doc.created_at).toLocaleString()}
          </span>
        )}
        {doc.user && me && doc.user.id !== me.id && (
          <span>
            · saved by {doc.user.username}
            {isAgent(doc.user) && " (agent)"}
          </span>
        )}
      </div>

      {!canEdit && (
        <p className="mb-2 text-sm text-muted-foreground">
          Shared with you by {doc.user?.username ?? "its owner"} to read and
          copy — only their side can edit it. Copy it to make your own.
        </p>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {doc.visibility && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SharingControl
            title={doc.title || "Untitled"}
            selectLabel="Who can see this document"
            visibility={doc.visibility}
            sharedWith={doc.shared_with}
            ownerName={doc.user?.username}
            canChangeVisibility={canEdit}
            canChangeSharing={Boolean(doc.can_change_sharing)}
            hints={DOCUMENT_VISIBILITY_HINTS}
            householdOption="Household — they can read and copy"
            shareDescription="The people you add — and their agents — can read it, find it in search and copy it into their own library. Only your side can edit it or change who it's shared with."
            idPrefix="document"
            onChange={changeVisibility}
            onShare={share}
          />
        </div>
      )}

      <div className="mb-4">
        <TagChips tags={doc.tags} />
      </div>

      {canDelete && doc.enrichment_status === "pending" && (
        <Alert className="mb-3">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              The AI summary and suggested tags haven&apos;t been generated yet.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEnrich}
              disabled={enriching}
            >
              {enriching ? (
                <>
                  <Loader2 className="animate-spin" />
                  Generating…
                </>
              ) : (
                "Generate now"
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {canDelete && doc.enrichment_status === "failed" && (
        <Alert className="mb-3">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              The AI summary couldn&apos;t be generated
              {doc.enrichment_error ? ` (${doc.enrichment_error})` : ""}. The
              document is saved and searchable regardless.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEnrich}
              disabled={enriching}
            >
              {enriching ? (
                <>
                  <Loader2 className="animate-spin" />
                  Retrying…
                </>
              ) : (
                "Retry"
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {enrichError && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{enrichError}</AlertDescription>
        </Alert>
      )}

      {doc.summary && (
        <Alert className="mb-3">
          <AlertDescription>
            <span className="font-semibold text-foreground">Summary:</span>{" "}
            {doc.summary}
          </AlertDescription>
        </Alert>
      )}

      {editing ? (
        <form onSubmit={handleSave} className="mb-4 flex flex-col gap-4">
          {saveError && (
            <Alert variant="destructive">
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="edit-title">Title</Label>
            <Input
              id="edit-title"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
            />
          </div>
          {isNote && (
            <div className="grid gap-1.5">
              <Label htmlFor="edit-markdown">Note (markdown)</Label>
              <Textarea
                id="edit-markdown"
                rows={14}
                className="max-h-[32rem]"
                value={editMarkdown}
                onChange={(event) => setEditMarkdown(event.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Saving re-indexes the note for search.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div
          className="markdown-content mb-4"
          // Server-sanitized canonical HTML, sanitized AGAIN client-side
          // (DOMPurify) per the spec's stored-XSS boundary; images are
          // stripped at render to avoid tracking-pixel requests.
          dangerouslySetInnerHTML={{
            __html: sanitizeDocumentHtml(doc.content),
          }}
        />
      )}

      {related.length > 0 && (
        <div className="mt-8 border-t pt-4">
          <h2 className="mb-2 font-display text-base font-semibold">
            Related in your library
          </h2>
          <ul className="m-0 list-none p-0">
            {related.map((item) => (
              <li key={item.id} className="mb-1">
                <Link
                  to={`/knowledge/documents/${item.id}`}
                  className="text-primary underline"
                >
                  {item.title || "Untitled"}
                </Link>{" "}
                <span className="text-sm text-muted-foreground">
                  {item.source_type === "url" ? "web page" : "note"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* An in-app dialog, not window.confirm: native dialogs are invisible
          to (and block) automated browser sessions, so the Delete button did
          nothing at all for an agent signed into the UI — which is now a
          supported way to use this app. Reported by the Pebo agent, who could
          not delete its own test note (task #24). */}
      <Dialog
        open={showDeleteModal}
        onOpenChange={(open) => !open && !deleting && setShowDeleteModal(false)}
      >
        <DialogContent
          showCloseButton={!deleting}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => deleting && event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {isNote ? "Delete note" : "Delete page"}
            </DialogTitle>
            <DialogDescription>
              Delete <strong>{doc.title || "Untitled"}</strong>?{" "}
              {isNote
                ? "This can't be undone."
                : "The archived copy goes with it. This can't be undone."}
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DocumentDetail;
